'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import { validateEmail, validatePhone } from '@paction-2.0/ui';
import {
  introMessageTooShortMessage,
  isIntroMessageAcceptable
} from '@paction-2.0/util/validation';

import { handleError } from '@/lib/api/errors';
import { trackFunnelEvent } from '@/lib/analytics';
import { useAuth } from '@/lib/auth/AuthProvider';
import { fetchSession } from '@/lib/auth/client';
import {
  buildAttributionPayload,
  storeMarketingParamsFromLocation
} from '@/features/auth/services/signupAttribution';
import { useContactVerification } from '@/features/verification';
import { markIntroMessagePromptPending } from '@/features/dialogs/IntroMessagePrompt/introMessagePromptPending';
import { INTRO_COPY_VARIANT } from '@/features/dialogs/IntroMessagePrompt/introCopy';
import {
  clearWaitlistIntroDraft,
  readWaitlistIntroDraft,
  saveWaitlistIntroDraft
} from './waitlistIntroDraft';
import {
  consumeWaitlistResumeToken,
  fetchResumedWaitlistForm
} from './waitlistResume';
import {
  describeMissingContactRequirements,
  hasStartedContactForm
} from '@/components/MissingRequirementsHint';
import { PROVINCE_OPTIONS } from '@/features/puppy-search/services/collapsibleSearchHeader';
import { getCitiesByProvince, type City } from '@/features/locations';

export const INTRO_SAVE_DELAY_MS = 1_500;
export const VERIFICATION_STEP = 'verification';

const extractValidationMessage = (details: unknown): string | undefined => {
  if (!details || typeof details !== 'object') return undefined;

  const d = details as {
    formErrors?: string[];
    fieldErrors?: Record<string, string[] | undefined>;
  };

  if (d.formErrors?.length) return d.formErrors[0];

  if (d.fieldErrors) {
    for (const msgs of Object.values(d.fieldErrors)) {
      if (msgs?.length) return msgs[0];
    }
  }

  return undefined;
};

export type GlobalWaitlistFormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  provinceSlug: string;
  citySlug: string;
  introMessage: string;
};

export type GlobalWaitlistFieldErrors = {
  firstName: string | null;
  email: string | null;
  phone: string | null;
  introMessage: string | null;
};

export type UseGlobalWaitlistWidgetProps = {
  breedSlug: string;
  breedName: string;
  provinceSlug?: string | null;
  provinceName?: string;
  citySlug?: string;
  cityName?: string;
  pageType?: string;
  hasLocalResults?: boolean;
};

export function useGlobalWaitlistWidget({
  breedSlug,
  breedName,
  provinceSlug: initialProvinceSlug,
  provinceName,
  citySlug: initialCitySlug,
  cityName,
  pageType,
  hasLocalResults
}: UseGlobalWaitlistWidgetProps) {
  const { session, status, setSession } = useAuth();
  const isAuthenticated = status === 'authenticated';
  const profile = session?.profile;

  const [form, setForm] = useState<GlobalWaitlistFormState>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    provinceSlug: initialProvinceSlug || '',
    citySlug: initialCitySlug || '',
    introMessage: ''
  });
  const [fieldErrors, setFieldErrors] = useState<GlobalWaitlistFieldErrors>({
    introMessage: null,
    firstName: null,
    email: null,
    phone: null
  });
  const [touched, setTouched] = useState<{
    firstName: boolean;
    email: boolean;
    phone: boolean;
    introMessage: boolean;
  }>({
    firstName: false,
    email: false,
    phone: false,
    introMessage: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [marketingEmailOptIn, setMarketingEmailOptIn] = useState(false);
  const [justSignedIn, setJustSignedIn] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [alreadyOnWaitlist, setAlreadyOnWaitlist] = useState(false);
  const [resumedFromEmail, setResumedFromEmail] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [submittedPhone, setSubmittedPhone] = useState<string | null>(null);
  const [automationResult, setAutomationResult] = useState<{
    providersConnected: number;
    conversationsCreated: number;
    pendingApproval: boolean;
  } | null>(null);

  const sessionIdRef = useRef<string>('');
  const formStartTrackedRef = useRef(false);
  const introSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  if (!sessionIdRef.current) {
    sessionIdRef.current = crypto.randomUUID();
  }

  useEffect(() => {
    storeMarketingParamsFromLocation(window.location.search);
    const draft = readWaitlistIntroDraft();
    if (draft) {
      setForm((prev) =>
        prev.introMessage ? prev : { ...prev, introMessage: draft }
      );
    }

    let cancelled = false;
    const resumeToken = consumeWaitlistResumeToken();
    if (resumeToken) {
      void fetchResumedWaitlistForm(resumeToken).then((saved) => {
        if (cancelled || !saved || saved.breedSlug !== breedSlug) return;
        sessionIdRef.current = saved.sessionId;
        setForm((prev) => ({
          ...prev,
          firstName: saved.firstName ?? prev.firstName,
          lastName: saved.lastName ?? prev.lastName,
          email: saved.email ?? prev.email,
          provinceSlug: saved.provinceSlug ?? prev.provinceSlug,
          citySlug: saved.citySlug ?? prev.citySlug,
          introMessage: saved.introMessage ?? prev.introMessage
        }));
        setResumedFromEmail(true);
      });
    }

    return () => {
      cancelled = true;
      if (introSaveTimerRef.current) clearTimeout(introSaveTimerRef.current);
    };
  }, [breedSlug]);

  const trackDropoff = useCallback(
    (
      lastFieldTouched?: string,
      overrides?: Partial<GlobalWaitlistFormState>
    ) => {
      const merged = { ...form, ...overrides };
      const attribution = buildAttributionPayload();
      const body = {
        sessionId: sessionIdRef.current,
        breedSlug,
        firstName: merged.firstName.trim() || undefined,
        lastName: merged.lastName.trim() || undefined,
        email: merged.email.trim() || undefined,
        phone: merged.phone.trim() || undefined,
        provinceSlug: merged.provinceSlug || undefined,
        citySlug: merged.citySlug || undefined,
        introMessage: merged.introMessage.trim() || undefined,
        introCatchphraseVariant: INTRO_COPY_VARIANT,
        lastFieldTouched,
        marketingAttribution: attribution
      };

      fetch('/api/v1.0/public/breed-interests/dropoff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).catch(() => {});
    },
    [breedSlug, form]
  );

  const citiesForProvince = useMemo((): City[] => {
    if (!form.provinceSlug) return [];
    return getCitiesByProvince(form.provinceSlug);
  }, [form.provinceSlug]);

  useEffect(() => {
    if (!profile) return;

    const nextFirst = profile.account?.firstName || '';
    const nextLast = profile.account?.lastName || '';
    const nextEmail = profile.email || '';
    const nextPhone = profile.account?.phone || '';

    setForm((prev) => ({
      ...prev,
      firstName: nextFirst || prev.firstName,
      lastName: nextLast || prev.lastName,
      email: nextEmail || prev.email,
      phone: nextPhone || prev.phone
    }));
  }, [profile]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      provinceSlug: initialProvinceSlug || prev.provinceSlug,
      citySlug: initialCitySlug || prev.citySlug
    }));
  }, [initialProvinceSlug, initialCitySlug]);

  const title = useMemo(() => {
    if (cityName) {
      return `Join the ${breedName} waitlist in ${cityName}`;
    }
    return provinceName
      ? `Join the ${breedName} waitlist in ${provinceName}`
      : `Join the ${breedName} waitlist`;
  }, [breedName, provinceName, cityName]);

  const description = useMemo(() => {
    if (!breedName) {
      return 'Tell us what you’re looking for and Paction will start matching you with trusted breeders across Canada.';
    }
    if (cityName) {
      return `Tell us what you’re looking for and Paction will start matching you with trusted ${breedName} breeders serving ${cityName} and across Canada.`;
    }
    return provinceName
      ? `Tell us what you’re looking for and Paction will start matching you with trusted ${breedName} breeders in ${provinceName} and across Canada.`
      : `Tell us what you’re looking for and Paction will start matching you with trusted ${breedName} breeders across Canada.`;
  }, [breedName, provinceName, cityName]);

  const handleChange = useCallback(
    (field: keyof GlobalWaitlistFormState) =>
      (
        e: React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      ) => {
        const value = e.target.value;
        setForm((prev) => {
          const next = { ...prev, [field]: value };
          if (field === 'provinceSlug' && value !== prev.provinceSlug) {
            // Default to the largest city in the province so users don't have
            // to make an extra selection. getCitiesByProvince returns cities
            // sorted by population descending, so [0] is the biggest city.
            const cities = getCitiesByProvince(value);
            next.citySlug = cities[0]?.slug ?? '';
          }
          return next;
        });

        if (field === 'firstName' && touched.firstName) {
          setFieldErrors((prev) => ({
            ...prev,
            firstName:
              value.trim().length < 3
                ? 'First name must be at least 3 characters'
                : null
          }));
        }
        if (field === 'email' && touched.email) {
          setFieldErrors((prev) => ({
            ...prev,
            email: value.trim() ? validateEmail(value.trim()) : null
          }));
        }
        if (field === 'phone' && touched.phone) {
          setFieldErrors((prev) => ({
            ...prev,
            phone: value.trim() ? validatePhone(value.trim()) : null
          }));
        }
        if (field === 'introMessage' && touched.introMessage) {
          setFieldErrors((prev) => ({
            ...prev,
            introMessage: isIntroMessageAcceptable(value)
              ? null
              : introMessageTooShortMessage
          }));
        }

        if (field === 'provinceSlug' || field === 'citySlug') {
          trackDropoff(field, { [field]: value });
        }

        if (field === 'introMessage') {
          saveWaitlistIntroDraft(value);
          if (introSaveTimerRef.current) {
            clearTimeout(introSaveTimerRef.current);
          }
          introSaveTimerRef.current = setTimeout(() => {
            trackDropoff('introMessage', { introMessage: value });
          }, INTRO_SAVE_DELAY_MS);
        }
      },
    [touched, trackDropoff]
  );

  const handleBlur = useCallback(
    (field: 'firstName' | 'email' | 'phone') => () => {
      setTouched((prev) => ({ ...prev, [field]: true }));

      if (!formStartTrackedRef.current) {
        formStartTrackedRef.current = true;
        trackFunnelEvent('waitlist_form_start', {
          signup_source: 'global_waitlist',
          breed_slug: breedSlug,
          province_slug: form.provinceSlug || undefined,
          city_slug: form.citySlug || undefined,
          surface: 'global_waitlist_widget',
          page_type: pageType,
          has_local_results: hasLocalResults
        });
      }

      const value = form[field].trim();
      if (field === 'firstName') {
        setFieldErrors((prev) => ({
          ...prev,
          firstName:
            value.length < 3 ? 'First name must be at least 3 characters' : null
        }));
      }
      if (field === 'email') {
        setFieldErrors((prev) => ({
          ...prev,
          email: value ? validateEmail(value) : null
        }));
      }
      if (field === 'phone') {
        setFieldErrors((prev) => ({
          ...prev,
          phone: value ? validatePhone(value) : null
        }));
      }

      trackDropoff(field);
    },
    [form, breedSlug, trackDropoff, pageType, hasLocalResults]
  );

  const handleFieldBlur = useCallback(
    (field: string) => () => {
      trackDropoff(field);
    },
    [trackDropoff]
  );

  const resetState = useCallback(() => {
    setSubmitError(null);
    setSubmitSuccess(false);
    setIsSubmitting(false);
  }, []);

  const submitJoin = useCallback(
    async (verificationToken?: string) => {
      const firstNameError =
        form.firstName.trim().length < 3
          ? 'First name must be at least 3 characters'
          : null;
      const emailError = form.email.trim()
        ? validateEmail(form.email.trim())
        : 'Email is required';
      const phoneError = form.phone.trim()
        ? validatePhone(form.phone.trim())
        : null;
      const introMessageError = isIntroMessageAcceptable(form.introMessage)
        ? null
        : introMessageTooShortMessage;

      if (firstNameError || emailError || phoneError || introMessageError) {
        setFieldErrors({
          firstName: firstNameError,
          email: emailError,
          phone: phoneError,
          introMessage: introMessageError
        });
        setTouched({
          firstName: true,
          email: true,
          phone: true,
          introMessage: true
        });
        return;
      }

      setIsSubmitting(true);
      setSubmitError(null);

      const selectedProvince = PROVINCE_OPTIONS.find(
        (p) => p.slug === form.provinceSlug
      );
      const selectedCity = citiesForProvince.find(
        (c) => c.slug === form.citySlug
      );

      const locationString = selectedCity
        ? `${selectedCity.name}, ${selectedCity.provinceAbbr}`
        : selectedProvince?.label || '';

      try {
        const response = await fetch('/api/v1.0/public/breed-interests', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            breedSlug,
            provinceSlug: form.provinceSlug || undefined,
            citySlug: form.citySlug || undefined,
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            email: form.email.trim(),
            phone: form.phone.trim() || undefined,
            location: locationString,
            introMessage: form.introMessage.trim() || undefined,
            verificationToken,
            marketingAttribution: buildAttributionPayload(),
            marketingEmailOptIn
          })
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));

          if (response.status === 403) {
            if (data.code === 'VERIFICATION_REQUIRED') {
              return 'verification_required' as const;
            }
            if (data.code === 'EMAIL_VERIFICATION_REQUIRED') {
              return 'email_verification_required' as const;
            }
          }

          if (response.status === 409) {
            if (data.code === 'MAX_BREED_INTERESTS_REACHED') {
              setSubmitError('You have joined the maximum number of waitlists');
              return;
            }
            setAlreadyOnWaitlist(true);
            setSubmittedEmail(form.email.trim());
            return;
          }

          const apiMessage =
            data.error ||
            data.message ||
            extractValidationMessage(data.details);

          throw new Error(apiMessage || 'Failed to join waitlist');
        }

        const responseData = await response.json().catch(() => ({}));
        if (responseData?.data?.automation) {
          setAutomationResult(responseData.data.automation);
        }

        if (responseData?.data?.auth) {
          await fetchSession()
            .then((nextSession) => {
              setSession(nextSession);
              if (nextSession) setJustSignedIn(true);
            })
            .catch(() => {});
        }

        if (!form.introMessage.trim()) {
          markIntroMessagePromptPending();
        }
        clearWaitlistIntroDraft();
        if (introSaveTimerRef.current) {
          clearTimeout(introSaveTimerRef.current);
          introSaveTimerRef.current = null;
        }
        setSubmitSuccess(true);
        setAlreadyOnWaitlist(true);
        setSubmittedEmail(form.email.trim());
        setSubmittedPhone(form.phone.trim() || null);
        trackDropoff('converted');
        trackFunnelEvent('waitlist_form_submit', {
          signup_source: 'global_waitlist',
          breed_slug: breedSlug,
          province_slug: form.provinceSlug || undefined,
          city_slug: form.citySlug || undefined,
          surface: 'global_waitlist_widget',
          page_type: pageType,
          has_local_results: hasLocalResults
        });

        if (!isAuthenticated) {
          setForm((prev) => ({
            ...prev,
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            introMessage: ''
          }));
        }
      } catch (err) {
        setSubmitError(
          handleError(
            err,
            '[GlobalWaitlist] Failed to join',
            'Unable to join waitlist'
          )
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isAuthenticated,
      breedSlug,
      form,
      citiesForProvince,
      trackDropoff,
      pageType,
      hasLocalResults,
      setSession,
      marketingEmailOptIn
    ]
  );

  const verificationRef = useRef<{
    switchChannel: (
      channel: 'phone' | 'email',
      destination: string
    ) => Promise<void>;
  } | null>(null);

  const verification = useContactVerification({
    onVerified: async (token) => {
      const outcome = await submitJoin(token);
      if (outcome === 'email_verification_required') {
        await verificationRef.current?.switchChannel(
          'email',
          form.email.trim()
        );
      }
    }
  });

  useEffect(() => {
    verificationRef.current = verification;
  });

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (alreadyOnWaitlist || isSubmitting) return;

      const outcome = await submitJoin();

      if (
        outcome !== 'verification_required' &&
        outcome !== 'email_verification_required'
      ) {
        return;
      }

      const phone = form.phone.trim();
      const email = form.email.trim();
      const preferred: { channel: 'phone' | 'email'; destination: string } =
        phone && outcome === 'verification_required'
          ? { channel: 'phone', destination: phone }
          : { channel: 'email', destination: email };

      trackDropoff(VERIFICATION_STEP);
      const result = await verification.start({
        channel: preferred.channel,
        destination: preferred.destination,
        firstName: form.firstName
      });

      if (result.alreadyVerified && result.token) {
        await submitJoin(result.token);
      }
    },
    [
      alreadyOnWaitlist,
      isSubmitting,
      form,
      submitJoin,
      verification,
      trackDropoff
    ]
  );

  const missingRequirements = useMemo(
    () => describeMissingContactRequirements(form),
    [form]
  );

  const isFormValid = missingRequirements.length === 0;

  const hasStartedForm = useMemo(() => hasStartedContactForm(form), [form]);

  const isFieldDisabled = isAuthenticated || alreadyOnWaitlist;

  return {
    form,
    handleChange,
    handleBlur,
    handleFieldBlur,
    fieldErrors,

    provinceOptions: PROVINCE_OPTIONS,
    cityOptions: citiesForProvince,

    isSubmitting,
    submitError,
    submitSuccess,
    alreadyOnWaitlist,

    marketingEmailOptIn,
    toggleMarketingEmailOptIn: () => setMarketingEmailOptIn((value) => !value),

    title,
    description,
    isFormValid,
    missingRequirements,
    hasStartedForm,
    isFieldDisabled,

    handleSubmit,
    resetState,
    verification,
    justSignedIn,

    isAuthenticated,
    breedName,
    breedSlug,
    submittedEmail,
    submittedPhone,
    automationResult,
    resumedFromEmail
  };
}
